<template lang="pug">
    .separator(v-if="field.type === 'separator'")
        span {{ field.name }}
        hr.separator
    el-form-item(
        v-else-if="['switch', 'inline-switch'].includes(field.type)"
        :label="field.type === 'switch' ? ' ' : ''"
        :class="field.type"
        :style="field.style"
    )
        //- inactive-color="#ff4949"
        el-switch(
            :active-text="$t(field.name)"
            :disabled="disabled"
            v-model="value"
        )
    .sf-field-wrap(v-else :style="field.style")
        .sf-field-input
            el-form-item(
                :label="$t(field.name)"
                :class="[field.type, { 'has-info': field.info }]"
            )
                el-color-picker(
                    v-if="field.type === 'colorpicker'"
                    size="medium"
                    v-model="value"
                    color-format="hex"
                    :predefine="optionValues"
                    :disabled="disabled"
                )
                el-input(
                    v-else-if="field.type === 'textfield'"
                    :placeholder="field.placeholder"
                    :disabled="disabled"
                    v-model="value"
                )
                el-input(
                    v-else-if="field.type === 'textarea'"
                    type="textarea"
                    :autosize="{ minRows: 4, maxRows: 40 }"
                    :placeholder="field.placeholder"
                    :disabled="disabled"
                    v-model="value"
                )
                monaco-editor(
                    v-else-if="field.type === 'code-editor:html'"
                    language="html"
                    :disabled="disabled"
                    :completions="completions"
                    :macros="macros"
                    v-model="value"
                )
                monaco-editor(
                    v-else-if="field.type === 'code-editor:twig'"
                    language="twig"
                    :disabled="disabled"
                    :min-height="field.minHeight || 138"
                    :completions="completions"
                    :macros="macros"
                    v-model="value"
                )
                monaco-editor(
                    v-else-if="['code-editor:js', 'code-editor:javascript'].includes(field.type)"
                    language="js"
                    :disabled="disabled"
                    :completions="completions"
                    v-model="value"
                )
                monaco-editor(
                    v-else-if="field.type === 'code-editor:json'"
                    language="json"
                    :disabled="disabled"
                    :completions="completions"
                    v-model="value"
                )
                monaco-editor(
                    v-else-if="field.type === 'code-editor:css'"
                    language="css"
                    :disabled="disabled"
                    :completions="completions"
                    v-model="value"
                )
                template(v-else-if="field.type === 'upload'")
                    el-upload.plus-box(
                        v-if="! image"
                        ref="upload"
                        drag multiple :action="uploadEndpoint"
                        accept="image/jpg, image/jpeg, image/png, image/svg+xml"
                        :headers="uploadHeaders"
                        :on-success="uploadSucceed"
                        :data="uploadData"
                        :disabled="disabled"
                    )
                        i.el-icon-plus
                        .el-upload__text(v-html="$t('upload.drop.plural')")
                        .el-upload__tip(slot="tip") {{ $t('upload.validation', { filetypes: 'jpg/png', size: '3mb' }) }}
                    .image-input(v-else :class="{ deleted: image.deleted }")
                        img(:src="src")
                        el-button.delete-image(
                            circle type="info" size="mini"
                            icon="el-icon-fa-trash-alt far"
                            @click="deleteImage"
                        )
                el-radio-group(
                    v-else-if="field.type === 'radio'"
                    :disabled="disabled"
                    v-model="value"
                )
                    el-radio-button(
                        v-for="option in field.options"
                        :key="`${field.key}:${option.key}`"
                        :label="option.key"
                        :value="option.key"
                    ) {{ option.value }}
                el-checkbox-group(
                    v-else-if="field.type === 'checkbox'"
                    :disabled="disabled"
                    v-model="value"
                )
                    el-checkbox-button(
                        v-for="option in field.options"
                        :key="`${field.key}:${option.key}`"
                        :label="option.key"
                        :value="option.key"
                        :name="option.key"
                    ) {{ option.value }}
                el-select(
                    v-else-if="field.type === 'select'"
                    :disabled="disabled"
                    :clearable="clearable"
                    v-model="value"
                )
                    el-option(
                        v-for="option in field.options"
                        :key="`${field.key}:${option.key}`"
                        :label="option.value"
                        :value="option.key"
                    )
                //- datepicker: Stores value as ISO 8601 with timezone offset (e.g., 2024-01-15T00:00:00+01:00)
                el-date-picker(
                    v-else-if="field.type === 'datepicker'"
                    v-model="dateValue"
                    type="date"
                    :placeholder="field.placeholder"
                    :format="$t('yyyy-MM-dd')"
                    :disabled="disabled"
                )
                //- datetimepicker: Stores value as ISO 8601 with timezone offset (e.g., 2024-01-15T14:30:00+01:00)
                el-date-picker(
                    v-else-if="field.type === 'datetimepicker'"
                    v-model="dateValue"
                    type="datetime"
                    :placeholder="field.placeholder"
                    :format="$t('yyyy-MM-dd') + ' HH:mm'"
                    :disabled="disabled"
                )
                el-select.is-full-width(
                    v-else-if="field.type === 'multi-select'"
                    :disabled="disabled"
                    v-model="values"
                    multiple
                    filterable
                )
                    el-option(
                        v-for="option in selectOptions"
                        :key="`${field.key}:${option.key}`"
                        :label="option.value"
                        :value="option"
                    )
                el-select.is-full-width(
                    v-else-if="field.type === 'tags'"
                    :key="`tags-${selectOptions.length}`"
                    :disabled="disabled"
                    v-model="values"
                    value-key="key"
                    multiple
                    filterable
                    allow-create
                    default-first-option
                )
                    el-option(
                        v-for="option in selectOptions"
                        :key="`${field.key}:${option.key}`"
                        :label="option.value"
                        :value="option"
                    )
                quill-editor(
                    v-else-if="field.type === 'rte'"
                    :placeholder="field.placeholder"
                    :disabled="disabled"
                    :options="quillConfig"
                    v-model="value"
                )
                .field-info(v-if="field.info")
                    i.el-icon-info
                    span {{ field.info }}
        .sf-field-comment(v-if="field.comment && field.comment.value")
            .tooltip(v-if="field.comment.type === 'tooltip'")
                el-popover(
                    placement="right"
                    width="280"
                    trigger="hover"
                )
                    template(#default)
                        span(v-html="field.comment.value")
                    el-button(slot="reference" round) ?
            span.inline(v-else) {{ field.comment.value }}
</template>

<script>
import _ from 'lodash'
import uniqid from 'uniqid';
import { formatISO } from 'date-fns';

import quillConfig from '@/quill/config'
import AuthApi from '@/store/api/system/auth';
import PageApi from '@/store/api/cms/page';
import { useAuthStore, useSettingsStore, useSetupStore, usePageStore } from '@/stores'

import CodeMirror from '@/components/CodeMirror'
import MonacoEditor from '@/components/MonacoEditor'
import NestedSet from '@/nested-set-lib'

export default {
    name: 'SettingField',
    data() {
        quillConfig.modules.link.pages = this.getPageOptions;

        return {
            fileList: [],
            quillConfig,
            // quillPostLinksFeatureEnabled: false
        };
    },
    props: {
        field: {
            type: Object,
            required: true,
        },
        settingValues: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false
        },
        completions: {
            type: Array,
            default: () => []
        },
        // Twig macros available to the current document context.
        // Shape: [{ name, signature?, snippet?, desc? }] — see MonacoEditor.
        macros: {
            type: Array,
            default: () => []
        },
    },
    components: {
        CodeMirror,
        MonacoEditor,
    },
    computed: {
        uploadHeaders() {
            return {
                authorization: `Bearer ${useAuthStore().token}`,
            }
        },
        template: {
            get() {
                return _.get(this.form, 'config.template')
            },
            set(value) {
                // store.commit(`settings/form/template`, value);
            }
        },
        index() {
            const fieldkey = this.field.key.toString();
            const template_key = this.form.config.template.id

            return this.form.settings.findIndex(function (item, i) {
                return item.key.toString() === fieldkey
                    && item.template_key.toString() === template_key
            });
        },
        image() {
            return _.find(this.form.images, { type: this.field.key })
        },
        src() {
            if (this.image) {
                const storageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`, '/')
                let table = 'images'
                if (this.image.upload) table = 'uploads'

                const token = AuthApi.getToken()
                return `${storageRoute}/${table}/${this.image.id}/${this.image.name}?token=${token}`
            }
        },
        uploadData() {
            let uploadData = {
                'context[type]': this.field.key
            }
            uploadData = {
                'context[type]': this.field.key,
                'context[model]': 'CityPages\\Cms',
                //TODO context-id checken
                'context[id]': '1'
            }

            return uploadData
        },
        value: {
            get() {
                return _.get(this.settingValues, this.field.key)
            },
            set(value) {
                const template_key = this.form.config.template.id

                // if (['multi-select', 'tags'].includes(this.field.type)) {
                //     if (value.length > 0) {
                //         const options = value.filter(el => this.field.options.find(option => option.key === el.key))
                //         value = options.map(el => ({ key: el.key, value: el.value }))
                //     } else {
                //         value = []
                //     }
                // }

                if (['checkbox'].includes(this.field.type)) {
                    if (value.length > 0) {
                        const options = value.filter(key => this.field.options.find(option => option.key === key))
                        value = options.toString().replaceAll(",", " ");
                    } else {
                        value = ''
                    }
                }

                if (['switch', 'inline-switch', 'radio'].includes(this.field.type)) {
                    if (value === true) {
                        value = 'true'
                    } else if (value === false) {
                        value = 'false'
                    }
                }

                if (this.index >= 0) {
                    useSettingsStore().setFormSetting({ index: this.index, value });
                } else {
                    useSettingsStore().addFormSetting({
                        id: uniqid('new:'),
                        key: this.field.key,
                        template_key,
                        value,
                    })
                }
            }
        },
        dateValue: {
            get() {
                const val = _.get(this.settingValues, this.field.key);
                return val ? new Date(val) : null;
            },
            set(value) {
                const template_key = this.form.config.template.id;
                const isoValue = value ? formatISO(value) : null;

                if (this.index >= 0) {
                    useSettingsStore().setFormSetting({ index: this.index, value: isoValue });
                } else {
                    useSettingsStore().addFormSetting({
                        id: uniqid('new:'),
                        key: this.field.key,
                        template_key,
                        value: isoValue,
                    });
                }
            }
        },
        values: {
            get() {
                return _.get(this.settingValues, this.field.key)
            },
            set(options) {
                const template_key = this.form.config.template.id

                // if (['multi-select', 'tags'].includes(this.field.type)) {
                switch (this.field.type) {
                    case 'multi-select':
                        if (options.length > 0) {
                            options = options.filter(el => this.field.options.find(option => option.key === el.key))
                                .map(el => ({ key: el.key, value: el.value }))
                        } else {
                            options = []
                        }
                        break;
                    case 'tags':
                        if (options.length > 0) {
                            options = options.map(el => {
                                let value = el
                                if (typeof value === 'object') value = value.value
                                return { key: value, value }
                            })
                        } else {
                            options = []
                        }
                        break;
                }

                if (this.index >= 0) {
                    useSettingsStore().setFormSetting({ index: this.index, options });
                } else {
                    useSettingsStore().addFormSetting({
                        id: uniqid('new:'),
                        key: this.field.key,
                        template_key,
                        options,
                    })
                }
            }
        },
        selectOptions() {
            let options = _.get(this.field, 'options') || [];
            if (this.field.type === 'tags' && this.values) options = [...options, ...this.values]
            return options;
        },
        clearable() {
            var options = _.get(this.field, 'options') || [];
            return !options.some(function (option) { return option.default; });
        },
        optionValues() {
            return _.map(this.selectOptions, 'value');
        },
        form() {
            return useSettingsStore().form;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        uploadEndpoint() {
            return useSetupStore().uploadEndpoint;
        },
        pageStore() {
            return usePageStore()
        },
        options() {
            return this.pageStore.options
        },
    },
    methods: {
        getPageOptions() {
            let menus = _.get(this.activeCms, 'config.template.menus', [])
            menus = _.cloneDeep(menus).sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0))

            const menuOptions = _.get(this.options, 'menu.items') || []

            let tree = []
            menus.forEach(menu => {
                let dir = { folder: menu.name, children: [] }
                let children = []

                const menuOption = _.find(menuOptions, { key: menu.key })
                let items = _.get(menuOption, 'menuItems', [])

                if (items.length) {
                    items = _.cloneDeep(items)
                    children = NestedSet.createTree(items)
                }

                dir.children = children
                if (children.length) tree.push(dir)
            })
            return tree
        },
        deleteImage() {
            const index = _.findIndex(this.form.images, { type: this.field.key })
            if (index >= 0) {
                useSettingsStore().markFormImageAsDeleted({ index })
            }
        },
        uploadSucceed(response, file) {
            let image = response
            image.upload = true

            if (typeof image.size === 'number') image.size = image.size.toString()
            delete image.hash
            delete image.dimensions

            setTimeout(() => {
                if (this.$refs.upload) {
                    this.$refs.upload.uploadFiles = this.$refs.upload.uploadFiles.filter(el => el.uid !== file.uid)
                }
            }, 1000)

            useSettingsStore().addFormImage({ image })
            //this.updateUploadHeaders()
        },
        async getPagesPaginated(searchString = '') {
            const categories = _.get(this.activeCms, 'config.template.categories') || [];
            let data = {
                filters: [{
                    "key": "title",
                    "like": searchString
                },
                {
                    "key": "cms",
                    "match": this.activeCms.id
                },
                {
                    "in": categories.map(el => el.key),
                    "key": "category_keys"
                }],
                variables: {
                    order: 'created_at',
                    direction: 'desc',
                    withCategory: true
                },
            };

            let pageData;
            await PageApi.fetchPagesPaginated(data).then(data => {
                pageData = data.items;
            });
            return pageData;
        },
    },
    mounted() {
        if (this.field.type === "rte") {
            const features = _.get(this.activeCms, 'config.template.features') || []
            // this.quillPostLinksFeatureEnabled = !!features.find(el => el === 'quill:post-links');
            if (!!features.find(el => el === 'quill:post-links')) {
                if (!quillConfig.modules.link.types.includes('posts')) {
                    quillConfig.modules.link.types.splice(1, 0, 'posts');
                }
                quillConfig.modules.link.posts = this.getPagesPaginated;
            }
        }
    }
}
</script>

<style lang="sass" scoped>
.row
    display: flex
    margin-bottom: 12px
.flex-m
    display: flex
    div
        margin-right: 10px
// .image-input
//     img
//         width: 100px
.textfield
    min-width: 100px
.separator
    flex-grow: 1
    display: flex
    font-weight: bold
    > hr
        margin-left: 12px
        border-top: 0
        border-bottom: 1px
        border-left: 0
        border-right: 0
        border-style: inset
        align-self: center
.quill-editor
    min-width: 400px
.image-input
    display: inline-block
    position: relative
    & > img
        max-height: 120px
        max-width: 100%
        width: auto
        border-radius: 3px
        transition: .15s filter ease, .15s opacity ease
    .el-button.delete-image,
    .el-button.restore-image
        position: absolute
        top: 5px
        right: 5px
        z-index: 2
        width: 30px
        height: 30px
        opacity: 0
        -webkit-transition: opacity .15s ease
        transition: opacity .15s ease
    &:hover
        .el-button.delete-image,
        .el-button.restore-image
            opacity: 1
    &.deleted
        & > img
            opacity: .6
            filter: grayscale(1) blur(1px)
.el-select
    width: 100%
.field-info
    margin-top: 6px
    font-size: 12px
    line-height: 1.3
    color: #909399
    display: flex
    align-items: flex-start
    gap: 6px
    i
        margin-top: 2px

.sf-field-wrap
    display: flex
    align-items: flex-start
    gap: 8px
    flex: 1

.sf-field-input
    flex: 1
    min-width: 0

.sf-field-comment
    .tooltip
        padding-top: 26px
        .el-button
            padding: 0
            height: 28px
            width: 28px
            line-height: 20px
            text-align: center
            font-weight: 500
            background: #f9fafc
            &:focus, &:active
                border-color: #dde0e6
                color: #606266
    .inline
        margin-top: 26px
        display: block
        font-size: 12px
        line-height: 1.4
        color: #909399
</style>
